import { api } from "@/api";
import appConfig from "@/config/appConfig";
import { changeTheme } from "@/redux/slices/theme.slice";
import { setActiveDashboard, setBusiness, setIsLogged, setUserDetails } from "@/redux/slices/user.slice";
import { Button, Checkbox, Form, Input, Spin } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import "./SignIn.css";
import { flare } from "@flareapp/js";

const onFinishFailed = (values) => {
  // eslint-disable-next-line no-console
  console.log("error", values);
};

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { businessUrl = "" } = useParams();

  const [loginData, setLoginData] = useState({});
  const [loading, setLoading] = useState(false);

  const getLoginData = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/business/user-login-data`, {
        params: {
          businessUrl,
          unique: true,
        },
      });
      setLoginData(data?.data);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("Login data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessUrl) {
      getLoginData();
    }
  }, [businessUrl]);

  const onFinish = async (login) => {
    try {
      const activeDeviceId = uuidv4();
      localStorage.setItem("activeDeviceId", activeDeviceId);
      await axios.post(
        `/user/login`,
        { ...login, email: login.email?.toLocaleLowerCase(), activeDeviceId },
        { withCredentials: true },
      );
      dispatch(setIsLogged(true));
      const userResponse = await axios.get(`/user/me`, { withCredentials: true });
      if (userResponse) {
        dispatch(setUserDetails(userResponse.data));
        dispatch(setActiveDashboard(userResponse.data?.dashboard || ""));
        if (userResponse.data?.theme) {
          dispatch(changeTheme(userResponse.data.theme === "dark"));
        }
        dispatch(setBusiness([]));
        localStorage.setItem("isLoggedIn", "yes");
        navigate("/");
      }
    } catch (error) {
      flare.report(error);
      await Swal.fire({
        title: "",
        text: error.response?.data?.message || "Credentials Information Wrong",
        icon: "error",
        timer: 2000,
        confirmButtonText: "OK",
      });
    }
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Sign In`;
  }, []);

  const background = loginData?.bgImage || "/img/bg.jpg";
  const logo = loginData?.logo || "/img/logo.png";
  const link = loginData?.mainSiteUrl || "https://ivistech.com";

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
        {" "}
        <Spin spinning={loading} />{" "}
      </div>
    );
  }

  return (
    <div>
      <div
        className="az-content-wrapper p-0"
        style={{ backgroundImage: `url(${background})`, backgroundAttachment: "fixed" }}
      >
        <div>
          <div className="sigIn_form">
            <div className="sigIn_card">
              <div className="signIn_inner">
                <h1 className="az-logo text-center" style={{ maxWidth: "230px", margin: "0 auto 40px" }}>
                  <a href={link} target="_blank" rel="noreferrer">
                    <img src={logo} style={{ maxHeight: "60px" }} alt="logo" className="img-fluid" />
                  </a>
                </h1>
                <div className="az-signin-header">
                  <Form
                    name="login"
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    form={form}
                    validateTrigger="onFinish"
                  >
                    <Form.Item
                      label="Email/Login ID"
                      name="email"
                      rules={[
                        {
                          required: true,
                          message: "Input Your Email/Login ID",
                        },
                      ]}
                    >
                      <Input placeholder="Input Your Email/Login ID" />
                    </Form.Item>

                    <Form.Item
                      label="Password"
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: "Input Your Password",
                        },
                      ]}
                    >
                      <Input.Password placeholder="Input Your Password" />
                    </Form.Item>

                    <div className="d-flex justify-content-end">
                      <p className="text-muted mb-1">
                        <Link to={`/forgot-password/${businessUrl}`} className="text-primary">
                          Forgot Password?
                        </Link>
                      </p>
                    </div>
                    <Form.Item
                      name="terms"
                      valuePropName="checked"
                      rules={[
                        {
                          validator: (_, value) =>
                            value
                              ? Promise.resolve()
                              : Promise.reject(new Error("You must accept the terms and conditions")),
                        },
                      ]}
                    >
                      <Checkbox>
                        You agree to our
                        <a
                          href="https://ivistech.com/term-of-use/"
                          rel="noreferrer"
                          target="_blank"
                          className="text-primary d-inline-block ms-1"
                        >
                          Terms & Conditions
                        </a>
                      </Checkbox>
                    </Form.Item>

                    <Form.Item shouldUpdate>
                      {({ getFieldValue }) => (
                        <Button
                          className="w-100 mb-2"
                          type="primary"
                          htmlType="submit"
                          disabled={!getFieldValue("terms")}
                        >
                          Sign In
                        </Button>
                      )}
                    </Form.Item>
                  </Form>
                </div>
                <div className="az-signin-footer text-center mt-2">
                  <p>Version {appConfig.version}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
